import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { Sheet, SheetContent } from "../../components/ui/sheet/index.js";
import { CONTACT_SHEET_OVERLAY_MOTION_CLASS } from "../contacts/contacts-table-presentation.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import { ReportsSidebar } from "./ReportsSidebar.js";

type ReportsWorkspaceValue = {
  organizationId: number;
  role: OrganizationMemberRoleName;
  openMobileNav: () => void;
  mobileNavOpen: boolean;
};

const ReportsWorkspaceContext = createContext<ReportsWorkspaceValue | null>(null);

export function useReportsWorkspace(): ReportsWorkspaceValue {
  const value = useContext(ReportsWorkspaceContext);
  if (!value) {
    throw new Error("useReportsWorkspace must be used inside ReportsWorkspaceLayout");
  }
  return value;
}

const PANE_OFFSET_PX = 12;
const PANE_MS = 180;

export function ReportsWorkspaceLayout({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const location = useLocation();
  const reduceMotion = useReducedMotion() ?? false;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const openMobileNav = useCallback((): void => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback((): void => setMobileNavOpen(false), []);

  const value = useMemo<ReportsWorkspaceValue>(
    () => ({ organizationId, role, openMobileNav, mobileNavOpen }),
    [organizationId, role, openMobileNav, mobileNavOpen],
  );

  const paneOffset = reduceMotion ? 0 : PANE_OFFSET_PX;

  return (
    <ReportsWorkspaceContext.Provider value={value}>
      <div className="flex h-full min-h-0 bg-canvas" data-testid="reports-workspace">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="start"
              className="flex h-full max-h-dvh w-[min(100%,22rem)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg lg:w-[20rem]"
              overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
              data-testid="reports-mobile-nav-sheet"
            >
              <ReportsSidebar
                variant="drawer"
                collapsed={false}
                onNavigate={closeMobileNav}
                onClose={closeMobileNav}
              />
            </SheetContent>
          </Sheet>

          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={location.pathname}
                className="absolute inset-0 min-h-0 overflow-hidden"
                initial={reduceMotion ? false : { opacity: 0, x: paneOffset }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: reduceMotion ? 0 : -paneOffset }}
                transition={{
                  duration: reduceMotion ? 0 : PANE_MS / 1000,
                  ease: "easeOut",
                }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </ReportsWorkspaceContext.Provider>
  );
}
